import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  DetachedRouteHandle,
  RouteReuseStrategy,
  destroyDetachedRouteHandle,
} from '@angular/router';

/**
 * Reúsa SELECTIVAMENTE componentes de listado para back/forward instantáneo
 * (efecto "Twitter": volver a la lista la muestra como estaba —scroll y
 * estado— sin recrear ni re-fetchear).
 *
 * Reglas:
 * - Solo cachea rutas con `data.reuse === true` (listados). Los detalles NO se
 *   cachean: siempre frescos por id.
 * - La clave del handle son los path params resueltos (NO los query params),
 *   para que /tienda/a y /tienda/b no colisionen y un cambio de ?page/?filters
 *   no invalide el componente vivo.
 * - LRU con tope + `componentRef.destroy()` al evictar: evita la fuga de
 *   memoria #1 de los RouteReuseStrategy. `clear()` se llama en logout.
 */
const MAX_CACHED = 8;

interface CacheEntry {
  readonly handle: DetachedRouteHandle;
}

@Injectable()
export class CachedReuseStrategy implements RouteReuseStrategy {
  private readonly cache = new Map<string, CacheEntry>();

  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    return this.isReusable(route);
  }

  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
    const key = this.keyOf(route);
    if (handle === null) {
      // Angular llama store(route, null) al RE-ADJUNTAR un handle. En ese
      // momento solo hay que quitarlo del mapa: destruirlo acá rompe el attach.
      this.cache.delete(key);
      return;
    }
    this.remember(key, handle);
  }

  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    return this.isReusable(route) && this.cache.has(this.keyOf(route));
  }

  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    if (!this.isReusable(route)) return null;

    const key = this.keyOf(route);
    const entry = this.cache.get(key);
    if (entry === undefined) return null;

    // Refresca posición LRU. El router puede llamar retrieve() en createNode y
    // de nuevo en activateRoutes antes de store(null); debe seguir disponible.
    this.cache.delete(key);
    this.cache.set(key, entry);
    return entry.handle;
  }

  shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    return future.routeConfig === curr.routeConfig;
  }

  /** Purga toda la cache (llamar en logout). */
  clear(): void {
    for (const key of [...this.cache.keys()]) {
      this.evict(key);
    }
  }

  private remember(key: string, handle: DetachedRouteHandle): void {
    const previous = this.cache.get(key);
    if (previous !== undefined && previous.handle !== handle) {
      this.destroy(previous.handle);
    }

    this.cache.delete(key); // refresca posición LRU
    this.cache.set(key, { handle });
    while (this.cache.size > MAX_CACHED) {
      const oldest = this.cache.keys().next().value;
      if (oldest === undefined) break;
      this.evict(oldest);
    }
  }

  private evict(key: string): void {
    const entry = this.cache.get(key);
    if (entry === undefined) return;
    this.destroy(entry.handle);
    this.cache.delete(key);
  }

  private destroy(handle: DetachedRouteHandle): void {
    destroyDetachedRouteHandle(handle);
  }

  private isReusable(route: ActivatedRouteSnapshot): boolean {
    return this.dataValue(route, 'reuse') === true;
  }

  private keyOf(route: ActivatedRouteSnapshot): string {
    const path = route.pathFromRoot
      .map((snapshot) => snapshot.url.map((segment) => segment.path).join('/'))
      .filter((segment) => segment.length > 0)
      .join('/');
    const feature = this.dataValue(route, 'feature');
    const discriminator = typeof feature === 'string' && feature.length > 0 ? feature : '';

    // `path: ''` dentro de un lazy child comparte URL con el padre lazy
    // componentless. Por eso el discriminator evita que dos RouteSnapshot
    // distintos con el mismo URL-path compitan por el mismo handle.
    return [route.outlet, path, discriminator].filter((part) => part.length > 0).join('::');
  }

  private dataValue(route: ActivatedRouteSnapshot, key: string): unknown {
    return (route.data as Record<string, unknown>)[key];
  }
}
